/**
 * Identity action confirmation ("the Knight arrives" design, 2026-07) — the one confirmation
 * mechanism covering register, login, and logout, anchored on the name slot. Two layers: a
 * transient text line under `.game-start-name-slot` (`role="status"`), and — for register/login
 * only — the slot's already-rendered Knight persona etching in. `logout` plays no etch: the
 * emptied slot plus the mark's absence is the visible change, the line just confirms it.
 *
 * The line's lifecycle is TIMER-owned, not animation-owned: append (CSS fade-in) -> a JS
 * `setTimeout` hold (~3.5s) -> add `--leaving` (CSS fade-out) -> remove on `animationend` with a
 * short `setTimeout` fallback. The hold must never ride an animation duration — the global
 * reduced-motion collapse (`skat.css` § Reduced Motion, `animation-duration: 0.01ms !important`)
 * would end an animation-driven hold instantly and the confirmation would flash away unread.
 * Under reduced motion the fades are instant, but the hold's timer still runs the full ~3.5s.
 * (Codex spec review finding — see the design's regression-test note.)
 *
 * Cross-reload survival: `stashIdentityFlash`/`consumeIdentityFlash` are the app's first
 * cross-reload flash message, riding `sessionStorage` — deliberately private to this module, not
 * a general toast component (see the design's Rejected alternatives). `game-start-auth.js` stashes
 * immediately before `submitLogin`/`submitLogout` reload; `game-start-ui.js#renderGameStart`
 * consumes (read-and-remove) on every render.
 *
 * Import direction: this module does NOT import `game-start-auth.js` (its importer — `name` is a
 * parameter precisely so a back-import would never be needed) or `icons/persona-icons.js` (the
 * persona element is built by `game-start-ui.js` / the identity overlay, which import the icon
 * family themselves; this module only ever finds the already-rendered element by selector).
 *
 * Full design: specs/2026-07/2026-07-09-identity-action-confirmation-design.md.
 */
import { t } from "../i18n.js";
import { shouldAnimateReveal } from "../animate.js";
import {
  ETCH_STAMP_DEFAULTS,
  prepareStamp,
  stageStamp,
  addStamp,
  shedStampResidue,
} from "../animation/components/etch-stamp.js";
import { gsap } from "../vendor/gsap.js";

const FLASH_KEY = "identityFlash";

/** Stash the kind that must survive a login/logout reload (sessionStorage — per-tab, so a
 *  stray tab never replays another tab's beat). Called immediately before
 *  `window.location.reload()` in `game-start-auth.js`. */
export function stashIdentityFlash(kind) {
  sessionStorage.setItem(FLASH_KEY, kind);
}

/** Read-and-remove: a re-render (session-lost banner, resize re-entry) can't replay the beat,
 *  and a token that somehow goes unplayed dies at the next game-start render rather than
 *  lingering. Returns the stashed kind, or null when nothing was stashed. */
export function consumeIdentityFlash() {
  const kind = sessionStorage.getItem(FLASH_KEY);
  if (kind != null) sessionStorage.removeItem(FLASH_KEY);
  return kind;
}

const CONFIRM_KEY = {
  register: "gameStartAuthConfirmRegister",
  login: "gameStartAuthConfirmLogin",
  logout: "gameStartAuthConfirmLogout",
};

// play-test: how long the line holds before it fades — a JS timer, never an animation duration
// (see the file header's reduced-motion guarantee).
const HOLD_MS = 3500;
// Belt-and-suspenders cap for the fade-out, mirroring overlay-shell.js's
// EXIT_ANIM_FALLBACK_MS: if `animationend` never fires (a 0-duration animation under reduced
// motion doesn't reliably emit it), the line is still removed by this deadline.
const LEAVE_FALLBACK_MS = 350;

const PERSONA_ETCH_CONFIG = {
  ...ETCH_STAMP_DEFAULTS,
  strokeNative: true, // the Knight bust draws its own strokes — no clones, no ink group
  stampScale: 1, // etch at rest size — the mark inscribes, it doesn't press
  stampPress: false, // etch-only, like the declarer-strip feat badges (declaration-strip-etch.js)
};

/**
 * Play the one-shot confirmation beat: append the transient line to the name slot, then — for
 * `register`/`login` only — etch the slot's persona in. `name` fills the `{0}` in
 * `gameStartAuthConfirmLogin`; the caller resolves it (both callers already have it — see the
 * file header on why this module never calls `getRegisteredName` itself).
 */
export function playIdentityConfirmation(kind, name) {
  const slot = document.querySelector(".game-start-name-slot");
  if (slot == null) return; // no game-start screen mounted to anchor the beat on
  slot.appendChild(buildConfirmationLine(kind, name));
  if (kind !== "logout") playPersonaEtch(slot);
}

function buildConfirmationLine(kind, name) {
  const line = document.createElement("div");
  line.className = "game-start-identity-confirmation";
  line.setAttribute("role", "status");
  line.textContent =
    kind === "login" ? t(CONFIRM_KEY[kind], name) : t(CONFIRM_KEY[kind]);
  armLineLifecycle(line);
  return line;
}

// append (CSS fade-in on mount, by the caller) -> hold (this JS timer) -> --leaving (CSS
// fade-out) -> remove, on `animationend` or the fallback timer, whichever fires first.
function armLineLifecycle(line) {
  setTimeout(() => {
    line.classList.add("game-start-identity-confirmation--leaving");
    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      line.remove();
    };
    line.addEventListener("animationend", remove, { once: true });
    setTimeout(remove, LEAVE_FALLBACK_MS);
  }, HOLD_MS);
}

// The Knight bust already rendered in the slot (game-start-ui.js#buildIdentityTrigger) etches
// in — found by selector, never rebuilt (see file header). Self-gated on shouldAnimateReveal();
// a missing host (no persona rendered, e.g. this beat firing over an anonymous row) is silently
// skipped, not thrown. A thrown etch is caught and warned per the animation-error convention —
// the confirmation line must never be lost because the flourish beside it broke.
function playPersonaEtch(slot) {
  if (!shouldAnimateReveal()) return;
  const host = slot.querySelector(".game-start-name-persona");
  if (host == null) return;
  // prepareStamp is pure measurement — a null return (jsdom / unmeasurable) leaves the mark
  // untouched, so it stays outside the try. staging onward mutates the mark, so a throw there
  // must restore it: unlike the declarer-strip precedent's ephemeral cells (rebuilt each refresh),
  // this Knight is persistent, and a half-staged failure would otherwise leave it hidden until the
  // next render. shedStampResidue returns it to the factory shape — the catch recovers, not just warns.
  const stamp = prepareStamp(host, PERSONA_ETCH_CONFIG);
  if (stamp == null) return;
  try {
    stageStamp(host, stamp, PERSONA_ETCH_CONFIG);
    const tl = gsap.timeline({
      onComplete: () => shedStampResidue(host, stamp),
    });
    addStamp(tl, host, stamp, PERSONA_ETCH_CONFIG);
  } catch (e) {
    console.warn("[game-start-identity-confirmation] etch failed:", e.message);
    shedStampResidue(host, stamp);
  }
}
